// Транспорт и хуки состава сделки — товарных строк Bitrix. ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/products/*).
// Смешивать их — значит однажды передать ListParams туда, где ждут строку запроса.
//
// Состав сделки правят в Bitrix, и записи здесь нет и появиться не должно: на изменение
// товарных строк завязаны бизнес-процессы (см. шапку products_api.py).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/products'

/**
 * Свод по всей базе закрыт этой роли. Отдельный тип, как `НеВладелец` в api/money.ts:
 * 403 здесь не поломка, а нормальный ответ сервера.
 *
 * BFF зовёт `rights.проверить_чтение_свода` (web/bff/main.py, ветка `/api/products/`):
 * расхождения считаются по ВСЕМ сделкам сразу, сузить их по зоне нечем, и урезанная
 * выборка ответила бы на другой вопрос молча. Поэтому отказ — и экран обязан объяснить
 * его словами, а не показать «Ошибка: forbidden» или, того хуже, пустую таблицу:
 * пустая таблица здесь читается как «расхождений нет».
 */
export class НетДоступа extends Error {
  constructor(причина = '') {
    super(причина || 'Этот свод считается по всем сделкам сразу — вашей роли он не открыт')
    this.name = 'НетДоступа'
  }
}

/** Тот же разбор ответа, что в api/client.ts и api/p73.ts: 401 → на логин, 400 → отказ ядра. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  if (res.status === 403) {
    // `detail` — объяснение самого BFF («считается по всем сделкам сразу — сузить его
    // нечем»). Оно точнее любого нашего текста: BFF знает, чем именно сужена зона.
    const rec = (await res.json().catch(() => ({}))) as Record<string, unknown>
    throw new НетДоступа(String(rec.detail ?? ''))
  }
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой запрос обслужить нельзя». Пустая таблица здесь читалась бы
    // как «состава нет», а это разные вещи.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. products_api.py) ───────────

/** Одна товарная строка сделки. */
export interface ТоварнаяСтрока {
  bitrix_id: number
  owner_id: number
  /** Позиция каталога Bitrix. null — строку вписали в сделку руками (у большинства строк). */
  product_id: number | null
  product_name: string | null
  /** Внешний код строки. На этом портале заполнен у единиц строк — привязки к 1С в данных нет. */
  xml_id: string | null
  quantity: number | null
  /** Итоговая цена за единицу (со скидкой, с налогом если tax_included). */
  price: number | null
  price_exclusive: number | null
  price_netto: number | null
  price_brutto: number | null
  discount_type_id: number | null
  discount_rate: number | null
  /** Скидка деньгами на ЕДИНИЦУ, а не на строку. */
  discount_sum: number | null
  /** Ставка НДС в процентах. null — налог у строки не задан вовсе. */
  tax_rate: number | null
  tax_included: boolean | null
  measure_code: number | null
  measure_name: string | null
  sort: number | null
  /** 1 — товар, 4 — услуга. Своего словаря не сочиняем, показываем как есть. */
  row_type: number | null
  customized: boolean | null
  /** Сумма строки = price * quantity. Считает ядро, чтобы это число было в одном месте. */
  amount: number | null
}

export interface ИтогиСостава {
  lines: number
  /** Итог по строкам. null — сделку ещё не опрашивали, итога не существует. */
  sum: number | null
  /** Строк, у которых цена или количество не пришли: в итог они не вошли. */
  lines_without_sum: number
  quantity_sum: number
  discount_sum: number
  from_catalog: number
}

/**
 * Код причины расхождения. Считает ядро (products_api.py, выражение ПРИЧИНА) — одним
 * SQL и для карточки, и для сводки. Здесь только перечисление, никакой своей логики:
 * повторить правило «во сколько раз» на клиенте значит завести второе место для одного
 * решения, и однажды экран и сводка назовут одну сделку по-разному.
 */
export type ПричинаРасхождения =
  | 'rounding'          // расходится меньше чем на рубль — хвост numeric
  | 'no_amount'         // сумма сделки не заполнена, а состав есть
  | 'no_price'          // позиции есть, цена не проставлена
  | 'no_rows'           // сумма стоит, а товарных строк нет вовсе
  | 'duplicate'         // состав задублирован: те же строки внесены несколько раз
  | 'part'              // сумма кратна составу: состав на один комплект из двух и т.п.
  | 'currency'          // сумма в рублях, а цена строк в валюте
  | 'catalog'           // состав в разы больше суммы: в сделку вложили подбор
  | 'small'             // разница меньше 5 % — торг или округление
  | 'not_recalculated'  // Bitrix обязан был пересчитать сумму по составу, и не сходится
  | 'partial'           // состав неполный: заведена часть позиций
  | 'over'              // состав дороже суммы — какая цифра верна, неизвестно

/**
 * Степень. Она решает, тревожить человека или нет, и в этом весь смысл разбора:
 * из 8 693 расхождений на боевой базе 28.07.2026 «error» только 702, а 7 058 — способ
 * вести учёт (сумму старых сделок не заводили, работы в состав не вписывают).
 */
export type СтепеньРасхождения = 'error' | 'check' | 'ok'

export interface РасхождениеСостава {
  /** Итог по строкам минус сумма сделки. Больше нуля — состав дороже, чем сумма сделки. */
  delta: number
  mismatch: boolean
  /** Код причины. null — расхождения нет. */
  code: ПричинаРасхождения | null
  /** Чинить, разобраться или не трогать. null — расхождения нет. */
  severity: СтепеньРасхождения | null
  /** Та же причина словами, как её формулирует ядро. null — не разошлось. */
  reason: string | null
}

export interface СоставСделки {
  deal_id: number
  /**
   * Спрашивали ли Bitrix про состав ЭТОЙ сделки.
   *
   * Ключевое поле. У товарной строки нет своей отметки времени, поэтому синк ходит
   * по сделкам; пока сделка не опрошена, «позиций нет» сказать НЕЛЬЗЯ — мы не знаем.
   * false + пустой список означает «ещё не заливалось», а не «состава нет».
   */
  scanned: boolean
  checked_at: string | null
  /** Сделку тронули после опроса — состав мог измениться, а мы этого ещё не видели. */
  scan_outdated: boolean
  deal: {
    title: string | null
    opportunity: number | null
    currency: string
    date_modify: string | null
  }
  totals: ИтогиСостава
  /** null — сделка не опрошена, расхождение считать не на чем. */
  diff: РасхождениеСостава | null
  rows: ТоварнаяСтрока[]
  /** Состав обрезан пределом ядра: итог по нему — не итог. */
  truncated: boolean
}

export interface СтрокаРасхождения {
  deal_id: number
  title: string | null
  opportunity: number | null
  currency: string | null
  assigned_by_id: number | null
  stage_id: string | null
  category_id: number | null
  date_create: string | null
  date_modify: string | null
  lines: number
  lines_sum: number
  delta: number
  reason: ПричинаРасхождения
  severity: СтепеньРасхождения
  /** Причина словами — от ядра, чтобы формулировка была одна на все экраны. */
  why: string
}

/** Одна куча разбора: сколько сделок и сколько денег. */
export interface КучаРасхождений {
  reason: ПричинаРасхождения
  severity: СтепеньРасхождения
  why: string
  deals: number
  money: number
}

/**
 * Разбор всех расхождений по кучам. Считается БЕЗ учёта фильтров намеренно: сводка
 * отвечает на вопрос «из чего сложились 2,8 млрд», и сузить её выбранным фильтром
 * значило бы спрятать от человека кучу, в которой лежит половина денег.
 */
export interface СводкаРасхождений {
  by_reason: КучаРасхождений[]
  by_severity: { severity: СтепеньРасхождения; deals: number; money: number }[]
  deals: number
  money: number
}

export interface СправочникиСостава {
  synced: { last_sync_at?: string | null; last_full?: string | null; last_count?: number | null;
            age_min?: number | null }
  counts: { rows_total: number; deals_with_rows: number; deals_scanned: number; deals_live: number }
  measures: Record<string, number>
  row_types: Record<string, number>
  data_notes: Record<string, string>
}

// ─────────── хуки ───────────
// staleTime большой: зеркало обновляется синком раз в пять минут, чаще спрашивать нечего.

const общее = { retry: 1, staleTime: 60_000 } as const

/** Состав одной сделки. Блок «Состав» в карточке сделки. */
export const useСоставСделки = (id: string | undefined) =>
  useQuery({
    queryKey: ['products', 'deal', id],
    queryFn: () => запрос<СоставСделки>(`/deal?id=${encodeURIComponent(id!)}`),
    enabled: !!id,
    ...общее,
  })

export interface ОтветРасхождений {
  rows: СтрокаРасхождения[]
  total: number
  next: number | null
  limit: number
  start: number
  summary: СводкаРасхождений
  /**
   * Словами ядра: к чему относится сводка. Она посчитана БЕЗ фильтров — и рядом со списком
   * одного человека стоял бы итог по всей базе, если этого не сказать вслух. Показывать
   * поле обязательно везде, где список сужен: замер 28.07.2026 — `assigned=6271` даёт 67
   * строк, а `summary.deals` в том же ответе 8 693.
   */
  summary_scope?: string
  /** Что ядро приняло из фильтров. `kind` разворачивается в список причин. */
  filters?: { kind: string[] | null; min: number | null; assigned: number[] | null }
}

/**
 * Сделки, где итог по составу расходится с суммой сделки. Экрана пока нет, ручка есть.
 *
 * `kind` принимает степень ('error' — то, что действительно надо чинить), код причины
 * или прежние 'no_amount'/'both'. Без kind список — 8 693 строки на 28.07.2026, и 81 %
 * из них не ошибка: читать это некому, начинать надо с 'error'.
 */
export const useРасхождения = (ф: { kind?: СтепеньРасхождения | ПричинаРасхождения | 'both';
                                    min?: number; start?: number; limit?: number
                                    /** id ответственного в Bitrix. Сужает СТРОКИ и `total`,
                                     *  но не `summary` — см. `summary_scope` в ответе. */
                                    assigned?: number | string } = {}) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null) continue
    p.set(k, String(v))
  }
  const qs = p.toString()
  return useQuery({
    queryKey: ['products', 'mismatches', ф],
    queryFn: () => запрос<ОтветРасхождений>(`/mismatches${qs ? `?${qs}` : ''}`),
    placeholderData: (prev) => prev,
    ...общее,
    // Отказ по правам повторять бессмысленно: второй запрос получит тот же 403, а человек
    // лишнюю секунду смотрит на скелетон вместо объяснения. Как retry: 0 в api/money.ts.
    retry: (счёт, e) => !(e instanceof НетДоступа) && счёт < 1,
  })
}

export const useСправочникиСостава = () =>
  useQuery({
    queryKey: ['products', 'refs'],
    queryFn: () => запрос<СправочникиСостава>('/refs'),
    staleTime: 10 * 60_000,
    retry: 1,
  })
